import { primitiveTokens } from "@appretio/ui-kit";
import type {
  AgCartesianAxisOptions,
  AgCartesianSeriesOptions
} from "ag-charts-enterprise";
import { useMemo } from "react";
import { ReviewDataResponse } from "../../../../pages/inSeasonReviewPage/hooks/useFetchReviewData";
import { RrpMetricsGroup } from "../../../../pages/inSeasonReviewPage/hooks/useFetchRrpKPIs";

/**
 * AXIS HEADINGS
 */
const X_NAME_AXES = "Recommended Retail Price (EURO)";
const Y_NAME_AXES_LEFT = "Sales vs Purchase (%)";
const Y_NAME_AXES_RIGHT = "Sellthrough (%)";

/**
 * DATA KEYS
 */
const KEY_RRP = "rrp";
const KEY_SALES_PCT = "sales_pct";
const KEY_PURCHASE_PCT = "purchase_pct";
const KEY_SELLTHROUGH_PCT = "sellthrough_pct";

/**
 * SERIES DISPLAY NAMES
 */
const SERIES_NAME_SALES = "Sales";
const SERIES_NAME_PURCHASE = "Purchase";
const SERIES_NAME_SELLTHROUGH = "Sellthrough";

const SELLTHROUGH_MARKER_SIZE = 6;

export type InSeasonReviewRow = ReviewDataResponse[number];

export interface UseRrpMetricsChartOptionsProps {
  /**
   * In-season review dataset
   *
   * Expected fields used here:
   * - style_recommended_retail_price_eur: number | string → price point (RRP)
   * - nis3: number | string → sales units
   * - time_purchase: number
   * - st: number (0..1) → sellthrough ratio; multiplied by 100 to become %
   */
  rrpKPIsData: RrpMetricsGroup[];
}

/**
 * Custom hook to generate chart options for RRP KPIs.
 * Renders a combination chart with:
 * - X-axis: Recommended Retail Price (RRP)
 * - Left Y-axis: Sales and Purchase mix (% of totals) as bar series
 * - Right Y-axis: Sellthrough (%) as line series
 */

export function useRrpKPIsChartOptions({
  rrpKPIsData
}: UseRrpMetricsChartOptionsProps) {
  return useMemo(() => {
    /** * PROCESS AND PREPARE DATA
     *
     * - Calculate total sales and purchase across all RRP groups.
     * - For each RRP group, calculate:
     *   - sales_pct: (group sales / total sales) * 100
     *   - purchase_pct: (group purchase / total purchase) * 100
     *   - sellthrough_pct: average sellthrough % .
     */

    const groups = rrpKPIsData
      .map((row) => ({
        rrp: Number(row.style_recommended_retail_price_eur),
        salesSum: Number(row.nis3_sum) || 0,
        purchaseSum: Number(row.time_purchase_sum) || 0,
        sellthroughAvg: (Number(row.st_avg) || 0) * 100
      }))
      .sort((a, b) => a.rrp - b.rrp);

    const totalSales = groups.reduce(
      (total, group) => total + group.salesSum,
      0
    );
    const totalPurchase = groups.reduce(
      (total, group) => total + group.purchaseSum,
      0
    );

    /**
     * - sales_pct / purchase_pct
     * - sellthrough_pct: average sellthrough % .
     */

    const data = groups.map(
      ({ rrp, salesSum, purchaseSum, sellthroughAvg }) => ({
        [KEY_RRP]: rrp,
        [KEY_SALES_PCT]: totalSales ? (salesSum / totalSales) * 100 : 0,
        [KEY_PURCHASE_PCT]: totalPurchase
          ? (purchaseSum / totalPurchase) * 100
          : 0,
        [KEY_SELLTHROUGH_PCT]: sellthroughAvg
      })
    );

    /**
     * SERIES VISIBILITY
     */
    const hasSalesData = data.some(
      (d) => typeof d[KEY_RRP] === "number" && d[KEY_SALES_PCT] > 0
    );
    const hasPurchaseData = data.some(
      (d) => typeof d[KEY_RRP] === "number" && d[KEY_PURCHASE_PCT] > 0
    );
    const hasSellthroughData = data.some(
      (d) => typeof d[KEY_RRP] === "number" && d[KEY_SELLTHROUGH_PCT] > 0
    );

    const series: AgCartesianSeriesOptions[] = [
      /**
       * Sales Mix (% of total sales)
       */
      {
        type: "bar",
        data: data,
        xKey: KEY_RRP,
        yKey: KEY_SALES_PCT,
        yName: SERIES_NAME_SALES,
        fill: primitiveTokens.colors.graph.blue[700],
        stroke: primitiveTokens.colors.graph.blue[700],
        cursor: "pointer",
        visible: hasSalesData
      },

      /**
       * Purchase Mix (% of total purchase)
       */
      {
        type: "bar",
        data: data,
        xKey: KEY_RRP,
        yKey: KEY_PURCHASE_PCT,
        yName: SERIES_NAME_PURCHASE,
        fill: primitiveTokens.colors.graph.red[700],
        stroke: primitiveTokens.colors.graph.red[700],
        cursor: "pointer",
        visible: hasPurchaseData
      },

      /**
       * Sellthrough (% of purchased units sold)
       */
      {
        type: "line",
        data: data,
        xKey: KEY_RRP,
        yKey: KEY_SELLTHROUGH_PCT,
        yName: SERIES_NAME_SELLTHROUGH,
        stroke: primitiveTokens.colors.graph.green[400],
        strokeWidth: 2,
        cursor: "pointer",
        visible: hasSellthroughData,
        marker: {
          size: SELLTHROUGH_MARKER_SIZE,
          stroke: primitiveTokens.colors.graph.green[400],
          fill: primitiveTokens.colors.graph.green[400]
        }
      }
    ];

    /**
     * AXES
     *
     * - X (category): formatted as price (2 decimals) to reflect distinct RRP points.
     * - Left Y (number): 0..100% not forced; “nice: false” keeps exact tick math for % labels.
     * - Right Y (number): fixed max 100 for sellthrough %, so the line always reads as a rate.
     */
    const baseAxes: AgCartesianAxisOptions[] = [
      /**
       * X Axis: RRP (EUR)
       */
      {
        type: "category",
        position: "bottom",
        title: { text: X_NAME_AXES },
        keys: [KEY_RRP],
        label: { formatter: ({ value }) => `${value.toFixed(2)}` }
      },
      /**
       * Left Y: Sales & Purchase mix (% of totals)
       */
      {
        type: "number",
        position: "left",
        title: { text: Y_NAME_AXES_LEFT },
        label: { formatter: ({ value }) => `${value.toFixed(2)}%` },
        keys: [KEY_SALES_PCT, KEY_PURCHASE_PCT],
        min: 0
      },
      /**
       * Right Y: Sellthrough % (bounded 0..100)
       */
      {
        type: "number",
        position: "right",
        title: { text: Y_NAME_AXES_RIGHT },
        label: { formatter: ({ value }) => `${value.toFixed(2)}%` },
        keys: [KEY_SELLTHROUGH_PCT],
        max: 100
      }
    ];

    return { series, axes: baseAxes };
  }, [rrpKPIsData]);
}
